import React, { Suspense, useEffect } from 'react';
import dayjs from 'dayjs';
// en is included by default
import 'dayjs/locale/es';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import Root from '@athena/spark/Root';
import { setDefaultBreakpoints, BreakpointProvider } from 'react-socks';
import { ErrorBoundary } from 'src/shared/components/ErrorBoundary';
import TelemetryProvider, { appInsights } from 'src/shared/lib/appInsights';
import { useFlags } from 'src/shared/components/LaunchDarkly';
import { AmplitudeProvider } from 'src/lib/amplitude';
import TelehealthErrorPage from 'src/components/page/TelehealthErrorPage';
import BrowserCheck from 'src/components/container/BrowserCheck';
import MainLayout from 'src/components/ui/Layouts/MainLayout';
import { getPlatformType } from 'src/components/utils/Helper';
import { initI18n } from 'src/i18n/index';
import useDisablePinchZoomEffect from 'src/lib/hooks/useDisablePinchZoomEffect';
import DefaultLayout from 'src/components/ui/Layouts/DefaultLayout';
import AppointmentProvider from 'src/components/provider/AppointmentProvider';
import BaseProviders from './BaseProviders';
import { TelehealthRoutes } from './TelehealthRoutes';
import { useRouteNameMapper } from './routeMap';
import './TelehealthWorkflow.scss';

const DeviceCheckLanding = React.lazy(
  () =>
    import(
      /* webpackChunkName: "device-check-landing" */
      /* webpackPrefetch: true */
      '../components/DeviceCheck/DeviceCheckLanding'
    )
);

const i18n = initI18n();

const renderError = () => {
  return (
    <DefaultLayout>
      <TelehealthErrorPage />
    </DefaultLayout>
  );
};

// TODO is there a way to pull in the Spark variables that define these? Or must we hardcode them.
setDefaultBreakpoints([{ mobile: 0 }, { tablet: 640 }, { desktop: 1040 }]);

const TelehealthWorkflow: React.FC = () => {
  const { flags } = useFlags();
  const platformType = getPlatformType();
  useDisablePinchZoomEffect();
  dayjs.locale(i18n.language);

  if (platformType === 'mobile') {
    // make sure the height of the app is recalculated when the url bar shows/hides
    // we need to do this to keep the control bar stuck to the bottom
    // https://developers.google.com/web/updates/2016/12/url-bar-resizing
    document.documentElement.classList.add('mobileFixedPosition');
    document.body.classList.add('mobileFixedPosition');
  }

  useEffect(() => {
    // once LaunchDarkly flags have been received, send them to App Insights as a custom event
    appInsights.trackEvent({ name: 'ldFlags', properties: flags });
  }, [flags]);

  return (
    <Root>
      <AmplitudeProvider
        apiKey={process.env.AMPLITUDE_KEY as string}
        // productName is set in each of the device-check and appointment workflows
        productName=""
        pageNameResolver={useRouteNameMapper}
      >
        <ErrorBoundary renderError={renderError}>
          <TelemetryProvider>
            <BrowserRouter>
              <BaseProviders>
                <ErrorBoundary name="MainErrorBoundary" renderError={renderError}>
                  <Routes>
                    <Route
                      path="/device-check/*"
                      element={
                        <MainLayout>
                          <Suspense fallback={<div></div>}>
                            <BreakpointProvider>
                              <BrowserCheck>
                                <DeviceCheckLanding />
                              </BrowserCheck>
                            </BreakpointProvider>
                          </Suspense>
                        </MainLayout>
                      }
                    />

                    <Route
                      path="*"
                      element={
                        <AppointmentProvider>
                          <MainLayout>
                            <BreakpointProvider>
                              <BrowserCheck>
                                <TelehealthRoutes />
                              </BrowserCheck>
                            </BreakpointProvider>
                          </MainLayout>
                        </AppointmentProvider>
                      }
                    />
                  </Routes>
                </ErrorBoundary>
              </BaseProviders>
            </BrowserRouter>
          </TelemetryProvider>
        </ErrorBoundary>
      </AmplitudeProvider>
    </Root>
  );
};

export default TelehealthWorkflow;
